/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { SpecTemplateStatus } from '@gh/builder-shared-data';
import {
  combineReducers,
  composeSetters,
  EMPTY_TABLE,
  entityValidationSetter,
  fieldValidationSetter,
  getInvalidFields,
  isEntityInvalid,
  onChangeReducer,
  setDirty,
  setEntityField,
  setEntityFields,
  setNestedEntityField,
  setPristine,
  setUpdatedDirtyType,
  Table,
  toOnChangeReducer,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import { arrayIterator } from '@gh/core-util';
import { find, first, flatten, fromPairs, keyBy, negate } from 'lodash';
import {
  getPbLinkCcElementId,
  getPbLinkCostCenterId,
  isPbLinkJstRecordEmpty,
  PbLinkCcElement,
  pbLinkCcElementTable,
  pbLinkCcTable,
  PbLinkJstGetResponse,
  PbLinkJstRecord,
  pbLinkJstRecordFromRef,
  pbLinkJstRecordToRef,
  PbLinkRecord,
  pbLinkRecordTable,
  validatePbLinkCcElementField,
  validatePbLinkJstRecord,
} from '../../resources/price-book-link.model';
import {
  PriceBookLinkJstState,
  PriceBookLinkStateEvent,
  PriceBookLinkStateEventType,
  queryPbLinkCcElement,
  queryPbLinkCcInvalidElements,
  queryPbLinkCostCenters,
  queryPbLinkCurrentRecords,
  queryPbLinkFirstCcElement, queryPbLinkJst,
  queryPbLinkSelectedCostCenter,
  queryPbLinkSelectedCostCenterId,
  queryPbLinkSelectedElement, queryPbLinkShowDeleted,
} from './price-book-link.state';

const mainFlowReducer = onChangeReducer(
  combineReducers({
    currentRecords: currentRecordsReducer,
  }),
  toOnChangeReducer(syncRefReducer),
  toOnChangeReducer(dirtyFlowReducer),
  toOnChangeReducer(globalFlagsReducer),
);

/**
 * Setter for first-order fields
 */
const setMainRecordField = (entity: PbLinkJstRecord, field: keyof PbLinkJstRecord, value: any): PbLinkJstRecord => {
  switch (field) {
    case 'paragraph':
      return setNestedEntityField(entity, ['paragraph', 'paragraphId'], value);
    case 'section':
      return setNestedEntityField(entity, ['section', 'sectionId'], value);
    default:
      return setEntityField(entity, field, value);
  }
};

/**
 * Setter for dependent fields, called only when first-order field has changed
 */
const setDependentRecordField = (entity: PbLinkJstRecord, field: keyof PbLinkJstRecord, value: any) => {
  switch (field) {
    case 'paragraph':
      return setEntityField(entity, 'section', setEntityFields(entity.section, {
        paragraphId: value,
        sectionId: void 0,
      }));
    default:
      return entity;
  }
};

// atomic setter of records fields
// it guarantees that record is consistent
const setRecordField = composeSetters(
  setMainRecordField,
  setDependentRecordField,
  setUpdatedDirtyType,
  entityValidationSetter(validatePbLinkJstRecord));

const setElementField = composeSetters(
  setEntityField,
  setDirty,
  fieldValidationSetter(validatePbLinkCcElementField));

export function priceBookLinkJstReducer(state: PriceBookLinkJstState,
                                        event: PriceBookLinkStateEvent): PriceBookLinkJstState {
  switch (event.type) {
    case PriceBookLinkStateEventType.Load:
      return loadDetails(state, <PbLinkJstGetResponse>event.init, event.mode);
    case PriceBookLinkStateEventType.LoadCostCenter:
      return loadCostCenter(state, event.costCenterId, event.elements);
    case PriceBookLinkStateEventType.SelectCostCenter:
    case PriceBookLinkStateEventType.SelectElement:
    case PriceBookLinkStateEventType.ResetRecordFocusedCell:
    case PriceBookLinkStateEventType.NavigateToInvalid:
      return selectionReducer(state, event);
    case PriceBookLinkStateEventType.SetRecordField:
      return mainFlowReducer(state, event);
    case PriceBookLinkStateEventType.ResetDirty:
      return setPristine(state);
    case PriceBookLinkStateEventType.ToggleShowDeleted:
      return filterReducer(state, event);
    default:
      return state;
  }
}

export function dirtyFlowReducer(state: PriceBookLinkJstState,
                                 event: PriceBookLinkStateEvent): PriceBookLinkJstState {
  switch (event.type) {
    case PriceBookLinkStateEventType.SetRecordField: {
      const element = queryPbLinkSelectedElement(state);
      const costCenter = queryPbLinkSelectedCostCenter(state);
      if (element && costCenter) {
        return setEntityFields(state, {
          costCenters: pbLinkCcTable.update(state.costCenters, setDirty(costCenter)),
          elements: pbLinkCcElementTable.update(state.elements, setDirty(element)),
        });
      }
      return state;
    }
    default:
      return state;
  }
}

function currentRecordsReducer(records: Table<PbLinkRecord>,
                               event: PriceBookLinkStateEvent): Table<PbLinkRecord> {
  switch (event.type) {
    case PriceBookLinkStateEventType.SetRecordField:
      return pbLinkRecordTable.mergeById(
        records,
        event.recordId,
        (record: PbLinkJstRecord) => setRecordField(record, event.name, event.value));
    default:
      return records;
  }
}

/**
 * Update list of references in current element if any of records was changed
 */
function syncRefReducer(state: PriceBookLinkJstState, event: PriceBookLinkStateEvent): PriceBookLinkJstState {
  const element = queryPbLinkSelectedElement(state);
  return element ?
    setEntityField(
      state,
      'elements',
      pbLinkCcElementTable.update(state.elements, setElementField(
        element,
        'jstLayout',
        queryPbLinkCurrentRecords(state).filter(negate(isPbLinkJstRecordEmpty)).map(pbLinkJstRecordToRef))))
    : state;
}

function globalFlagsReducer(state: PriceBookLinkJstState, event: PriceBookLinkStateEvent): PriceBookLinkJstState {
  switch (event.type) {
    case PriceBookLinkStateEventType.SetRecordField:
      return setDirty(state);
    default:
      return state;
  }
}

function filterReducer(state: PriceBookLinkJstState, event: PriceBookLinkStateEvent): PriceBookLinkJstState {
  switch (event.type) {
    case PriceBookLinkStateEventType.ToggleShowDeleted:
      return setShowDeleted(state, !state.filter.isShowDeleted);
    default:
      return state;
  }
}

function selectionReducer(state: PriceBookLinkJstState, event: PriceBookLinkStateEvent): PriceBookLinkJstState {
  switch (event.type) {
    case PriceBookLinkStateEventType.SelectCostCenter:
      return selectCostCenter(state, event.costCenterId);
    case PriceBookLinkStateEventType.SelectElement:
      return selectElement(state, event.elementId);
    case PriceBookLinkStateEventType.ResetRecordFocusedCell:
      return setNestedEntityField(state, ['selection', 'recordFocusedCell'], void 0);
    case PriceBookLinkStateEventType.NavigateToInvalid:
      return navigateToInvalid(state);
    default:
      return state;
  }
}

function navigateToInvalidElement(state: PriceBookLinkJstState, invalid: PbLinkCcElement): PriceBookLinkJstState {
  const firstInvalidRef = first(invalid.jstLayout.filter(isEntityInvalid));
  if (firstInvalidRef) {
    const invalidField = first(getInvalidFields(firstInvalidRef));
    const { specTemplateId } = firstInvalidRef;
    const stateWithFocusedField = invalidField ? focusTemplateCell(
      selectElement(selectCostCenter(state, invalid.costCenterId), getPbLinkCcElementId(invalid)),
      { id: specTemplateId, field: invalidField }) : state;
    const jst = queryPbLinkJst(state, specTemplateId);
    // show removed JST if it was hidden
    if (!queryPbLinkShowDeleted(state) && jst && jst.status === SpecTemplateStatus.Deleted) {
      return setShowDeleted(stateWithFocusedField, true);
    } else {
      return stateWithFocusedField;
    }
  } else {
    return state;
  }
}

function navigateToInvalid(state: PriceBookLinkJstState): PriceBookLinkJstState {
  const selectedElement = queryPbLinkSelectedElement(state);
  const selectedCostCenterId = queryPbLinkSelectedCostCenterId(state);

  // try to find invalid reference in the current selected element
  if (selectedElement && isEntityInvalid(selectedElement)) {
    return navigateToInvalidElement(state, selectedElement);
  }

  // try to find invalid element in the current selected cost center
  if (selectedCostCenterId) {
    const invalidElement = first(queryPbLinkCcInvalidElements(state, selectedCostCenterId));
    if (invalidElement) {
      return navigateToInvalidElement(state, invalidElement);
    }
  }

  const invalidCc = find(
    queryPbLinkCostCenters(state),
    (cc) => queryPbLinkCcInvalidElements(state, getPbLinkCostCenterId(cc)).length > 0);
  const invalid = invalidCc ? first(queryPbLinkCcInvalidElements(state, getPbLinkCostCenterId(invalidCc))) : void 0;
  return invalid ? navigateToInvalidElement(state, invalid) : state;
}

function focusTemplateCell(state: PriceBookLinkJstState, focusedCell?: FocusedCell): PriceBookLinkJstState {
  return setNestedEntityField(state, ['selection', 'recordFocusedCell'], focusedCell);
}

function selectElement(state: PriceBookLinkJstState, elementId?: ID): PriceBookLinkJstState {
  const stateWithSelectedElement = setNestedEntityField(state, ['selection', 'elementId'], elementId);

  return setEntityField(
    stateWithSelectedElement,
    'currentRecords',
    elementId ? buildRecordTable(state, elementId) : EMPTY_TABLE);
}

function selectCostCenter(state: PriceBookLinkJstState, costCenterId: ID): PriceBookLinkJstState {
  const stateWithSelectedCostCenter = setNestedEntityField(state, ['selection', 'costCenterId'], costCenterId);
  const firstElement = queryPbLinkFirstCcElement(stateWithSelectedCostCenter, costCenterId);
  return selectElement(stateWithSelectedCostCenter, firstElement ? getPbLinkCcElementId(firstElement) : void 0);
}

function loadCostCenter(state: PriceBookLinkJstState,
                        costCenterId: ID,
                        elements: PbLinkCcElement[]): PriceBookLinkJstState {
  return setEntityFields(state, {
    costCenters: pbLinkCcTable.mergeById(
      state.costCenters,
      costCenterId,
      (costCenter) => ({ ...costCenter, preloaded: true })),
    elements: pbLinkCcElementTable.insertAll(state.elements, elements),
  });
}

function loadDetails(state: PriceBookLinkJstState,
                     init: PbLinkJstGetResponse,
                     formMode?: FormMode): PriceBookLinkJstState {
  const { priceBookId, priceBookName, jstLayout } = init;
  const paragraphs = flatten(jstLayout.map((layout) => layout.paragraphs));
  const sections = flatten(paragraphs.map((paragraph) => paragraph.sections));
  const sectionToParagraph = fromPairs(flatten(paragraphs.map((paragraph) =>
    paragraph.sections.map((section) => [section.id, paragraph]))));

  return {
    ...state,
    formMode: formMode || state.formMode,
    priceBookId,
    masterInfo: { priceBookName },
    costCenters: pbLinkCcTable.create(arrayIterator(init.costCenters)),
    elements: pbLinkCcElementTable.create(),
    templates: keyBy(jstLayout, 'id'),
    paragraphs: keyBy(paragraphs, 'id'),
    sections: keyBy(sections, 'id'),
    sectionToParagraph,
    isDirty: false,
  };
}

function buildRecordTable(state: PriceBookLinkJstState, elementId: ID): Table<PbLinkJstRecord> {
  const { templates } = state;
  const element = queryPbLinkCcElement(state, elementId);
  if (element) {
    const { jstLayout } = element;
    // Construct array of records for all available templates.
    // If element does not have ref to some template, record is generated without paragraph and section id
    const records = Object.keys(templates).map((specTemplateId) => {
      const jst = templates[specTemplateId];
      return pbLinkJstRecordFromRef(
        specTemplateId,
        jst.name,
        jst.status === SpecTemplateStatus.Active,
        find(jstLayout, { specTemplateId }));
    });
    return <Table<PbLinkJstRecord>>pbLinkRecordTable.create(arrayIterator(records));
  } else {
    return EMPTY_TABLE;
  }
}

function setShowDeleted(state: PriceBookLinkJstState, isShowDeleted: boolean): PriceBookLinkJstState {
  return setNestedEntityField(state, ['filter', 'isShowDeleted'], isShowDeleted);
}
